




























































import { UserCreditData } from '@/pages/home/types';
import { HaiXiaoQueryStatusType } from '@/api/homeV2Status/types';
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import useDispatchProcess from '@/hooks/useDispatchProcess';
import {
  onMounted,
  defineComponent,
  PropType,
  computed,
  defineAsyncComponent,
} from '@vue/composition-api';
import { Button } from '@hb/coffee';
import { TriangleFill12 } from '@hb/coffee-icons';
import store from '@/store';
import { ubtSendError } from '@/shared/utils/ubt';
import { dispatchActions } from '@/store/dispatchActions';
import { ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { TP_HOME_GO_APPLY_CLICK, TP_HOME_GO_CREDIT_BLOCK_BY_TEMP_AMOUNT, TP_HOME_GO_CREDIT_CLICK } from '@/pages/home/tianqi';
import { PreCreditUserSourceEnums } from '@/enums/home-type';
import { ModuleConfig } from '@/pages/home/config/types';
import { isCreditTaskOcrSuspend } from '@/pages/home/utils';
import platform from '@/shared/utils/platform';
import { useAmountButtonAb } from './useAmountButtonAb';

const ActionBtnCornerAdv = defineAsyncComponent(
  () => import('@/pages/home/components/FinanceCard/components/ActionBtnCornerAdv/index.vue'),
);

export default defineComponent({
  name: 'ActionButtons',
  components: {
    Button,
    ActionBtnCornerAdv,
    TriangleFill12,
  },
  props: {
    userCreditData: {
      type: Object as PropType<UserCreditData>,
      required: true,
    },
    isAutarkyForbidCredit: {
      type: Boolean,
      required: true,
    },
    hasHangAndProgressingTask: {
      type: Boolean,
      required: true,
    },
    ubtCommonParams: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
    moduleConfig: {
      type: Object as PropType<ModuleConfig>,
      required: true,
    },
    haiXiaoQueryData: {
      type: Object as PropType<HaiXiaoQueryStatusType>,
      required: true,
    },
  },
  setup(props) {
    /** 是否促提现-预授信用户 */
    const isApplyPreCredit = computed(() => props.userCreditData.preCredit);
    /** 海消用户是否隐藏授信申请入口 */
    const isHaiXiaoApplyCredit = computed(() => props.haiXiaoQueryData?.hideApplyCreditEntry);
    /** 是否展示“最终可借额度”声明文案 */
    const showQuotaImpreciseTip = computed(
      () => props.userCreditData.preCredit
      && [PreCreditUserSourceEnums.NORMAL, PreCreditUserSourceEnums.SEA_CONSUMER]
        .includes(props.userCreditData.preCreditUserSource),
    );
    /** 授信、提现流程分发 */
    const { applyProcessDispatch, creditProcessDispatch } = useDispatchProcess();

    /** 首页授信按钮实验 hooks */
    const {
      amountButtonText,
      showPulseAnimation,
      getAmountButtonTextAb,
      getAmountButtonAnimationAb,
    } = useAmountButtonAb();

    /** 哈啰APP内，是否展示 “申请额度” 按钮 */
    const showAmountApplyButton = computed(() => {
      const {
        preCredit,
        creditStatus,
        creditAmount,
        hasOrder,
        firstInfoFillInTheInformation,
        firstInfoComplete,
        activationProgress,
        progressingTaskId,
      } = props.userCreditData;

      // ========== 提前排除场景 ==========
      // 非 哈啰出行 不进行按钮实验
      if (!platform.isEasyBike) {
        return false;
      }

      // 1. 预授信用户不展示"申请额度"按钮
      if (preCredit) {
        return false;
      }

      // 2. 锁客用户且有授信记录时不展示
      if (props.isAutarkyForbidCredit && creditStatus) {
        return false;
      }

      // ========== 场景一：无授信成功记录 ==========
      if (!creditStatus) {
        // 审核中的场景不展示按钮
        if (progressingTaskId) {
          return false;
        }

        // 判断是否满足首次申请额度的条件
        const { ocrAfterLoan } = store.state.base.businessInfoExtra?.userInfo || {};
        return firstInfoFillInTheInformation === UserInfoStatus.Init
          || firstInfoComplete
          || ocrAfterLoan;
      }

      // ========== 场景二：有授信记录且可以授信(非锁客) ==========
      if (creditStatus) {
        // 有额度 或 有订单 或 有进行中任务 时不展示
        if (Number(creditAmount) || hasOrder || progressingTaskId) {
          return false;
        }

        // 资料全部填写完 或 从未填写过时展示
        return activationProgress === 100 || firstInfoFillInTheInformation === UserInfoStatus.Init;
      }

      return false;
    });

    /** 是否展示“去授信”按钮，实际文案由业务决定 */
    const showCreditButtonText = computed(() => {
      const {
        firstInfoFillInTheInformation,
        firstInfoComplete,
        activationProgress,
      } = props.userCreditData;

      /** 预授信(授信、提现一体)用户，或海消用户隐藏授信申请入口，不展示“申请额度”按钮 */
      if (isApplyPreCredit.value || isHaiXiaoApplyCredit.value) {
        return '';
      }

      /** 无授信成功记录，可展示“申请额度”按钮 */
      if (!props.userCreditData.creditStatus) {
        /** 自营审核中(“联营”挂起状态)，提醒用户“补充资料” */
        if (props.hasHangAndProgressingTask) {
          return isCreditTaskOcrSuspend(props.userCreditData)
            ? '补充身份证，提交额度申请'
            : '去补充资料';
        }

        /** 授信审核中，提醒用户“耐心等待” */
        if (props.userCreditData.progressingTaskId) {
          return '额度审批中，请耐心等待';
        }

        const { ocrAfterLoan } = store.state.base.businessInfoExtra?.userInfo || {};
        /** 资料还未开始填写、第一阶段已填写完整、命中ocr后置提现流程，则展示“申请额度”文案 */
        if (
          firstInfoFillInTheInformation === UserInfoStatus.Init
          || firstInfoComplete
          || ocrAfterLoan
        ) {
          return '申请额度';
        }

        return '只差一步，继续申请';
      }

      /** 有授信记录 & 可以授信(非锁客)：授信申请流程 */
      if (!props.isAutarkyForbidCredit && props.userCreditData.creditStatus) {
        /**
         * 无额度 & 无借款订单
         * 若有授信申请，则展示“查看审核进度”按钮
         * 若无授信申请，则展示“申请额度”按钮
         */
        if (!Number(props.userCreditData.creditAmount) && !props.userCreditData.hasOrder) {
          if (props.userCreditData.progressingTaskId) {
            return '查看审核进度';
          }
          /** 若资料已全部填写完、从未填写过，才显示申请额度 */
          return activationProgress === 100 || firstInfoFillInTheInformation === UserInfoStatus.Init
            ? '申请额度' : '继续激活';
        }

        /** 有额度情况，若已有借款订单，可展示“申请提额”按钮(未提现过的用户，期望优先去提现) */
        if (props.userCreditData.hasOrder) {
          const { tempAmountStatus, tempTimeout } = store.state.tempAmount;
          /** 若点击“申请提额”按钮触发 临额申请中，则展示“提额中 x秒”等待临额下发 */
          return (tempAmountStatus?.tempAmountApplying && tempTimeout) ? `提额中 ${tempTimeout}s` : '申请提额';
        }
      }

      return '';
    });

    /** 去授信按钮 结合 “申请额度” 按钮实验结果 返回文案 */
    const creditButtonText = computed(() => {
      if (showAmountApplyButton.value) {
        return amountButtonText.value;
      }

      return showCreditButtonText.value;
    });

    /** “申请额度” 按钮实验结果 返回是否展示呼吸动画 */
    const showAmountPulseAnimation = computed(
      () => showAmountApplyButton.value && showPulseAnimation.value,
    );

    /** 是否展示“去提现”按钮 */
    const showApplyButton = computed(
      () => (
        props.userCreditData.creditStatus
        && props.userCreditData.hasEnter
        && Number(props.userCreditData.creditAmount) > 0)
      || isApplyPreCredit.value,
    );

    /** 是否以“按钮组”样式展示，即“去授信”和“去提现”按钮同时展示 */
    const isButtonGroupStyle = computed(() => showCreditButtonText.value && showApplyButton.value);

    /** “去授信”按钮点击事件 */
    const onCreditButtonClick = async (): Promise<void> => {
      try {
        /** “去授信”点击埋点 */
        ubtSetupPageButton(TP_HOME_GO_CREDIT_CLICK, props.ubtCommonParams);

        if (!props.userCreditData.creditStatus || props.userCreditData.preCredit) {
          creditProcessDispatch();
        } else {
          /**
           * 若用户有授信额度，则先请求check临额下发，若可进入申请流程，则中断当前提现流程
           * creditApplyTempAmount 返回false，进入临额申请流程
           */
          const applyTempAmountFailed = await dispatchActions.tempAmount.creditApplyTempAmount();

          if (applyTempAmountFailed) {
            creditProcessDispatch();
          } else {
            /** 若临额申请成功，则拦截当前流程，在此上报埋点 */
            ubtSetupPageButton(TP_HOME_GO_CREDIT_BLOCK_BY_TEMP_AMOUNT, props.ubtCommonParams);
          }
        }
      } catch (error) {
        ubtSendError(error as Error);
        creditProcessDispatch();
      }
    };

    /** “去提现”按钮点击事件 */
    const onApplyButtonClick = () => {
      /** “去提现”点击埋点 */
      ubtSetupPageButton(TP_HOME_GO_APPLY_CLICK, props.ubtCommonParams);

      applyProcessDispatch();
    };

    onMounted(() => {
      // 哈啰APP内展示“申请额度”按钮，发起实验
      if (showAmountApplyButton.value) {
        getAmountButtonTextAb();
        getAmountButtonAnimationAb();
      }
    });

    return {
      showCreditButtonText,
      showApplyButton,
      creditButtonText,
      showAmountPulseAnimation,
      isApplyPreCredit,
      showQuotaImpreciseTip,
      isButtonGroupStyle,

      onCreditButtonClick,
      onApplyButtonClick,
    };
  },
});
